import { Button, Card, Chip, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as api from '../services/api';
import { track } from '../utils';

const systemFontFamily =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const useStyles = makeStyles((theme) => ({
  card: {
    fontFamily: systemFontFamily,
    '& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root': {
      fontFamily: 'inherit',
      letterSpacing: 'normal',
    },
    border: '1px solid #B6D4FE',
    borderRadius: theme.spacing(2),
    backgroundColor: '#E9F2FD',
    padding: theme.spacing(1.5),
    boxShadow: 'none',
    width: '100%',
    marginTop: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1.5),
  },
  geminiIcon: {
    width: '20px',
    height: '20px',
    marginRight: theme.spacing(1.5),
    marginTop: '2px',
    flexShrink: 0,
  },
  buttonIcon: {
    width: '16px',
    height: '16px',
    marginRight: '8px',
  },
  spinningIcon: {
    width: '16px',
    height: '16px',
    marginRight: '8px',
    animation: '$spin 2s linear infinite',
    filter: 'brightness(0) invert(1)',
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  headerContent: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: '16px',
    lineHeight: '22.4px',
    color: '#333333',
    paddingBottom: theme.spacing(0.5),
    fontWeight: 700,
  },
  subtitle: {
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '21px',
    color: '#333333',
  },
  betaBadge: {
    backgroundColor: '#C7DFFC',
    color: '#333333',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '16.8px',
    height: '20px',
    borderRadius: '10px',
    marginLeft: theme.spacing(1),
    flexShrink: 0,
  },
  button: {
    padding: '16px 16px',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '22.4px',
    minWidth: '180px',
    maxWidth: '100%',
    height: '44px',
    marginLeft: theme.spacing(1),
    minHeight: '30px',
    boxShadow: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
  },
  generateButton: {
    backgroundColor: '#4390FF',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#4390FF',
      boxShadow: 'none',
    },
  },
  generatingButton: {
    '&.Mui-disabled': {
      backgroundColor: '#4390FF',
      color: '#FFFFFF',
      opacity: 0.7,
    },
  },
  resultContainer: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #B6D4FE',
    borderRadius: '8px',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  overviewText: {
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '21px',
    color: '#333333',
    margin: 0,
  },
  disclaimer: {
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: '18px',
    color: '#333333',
    textAlign: 'center',
    margin: theme.spacing(0.5, 0, 0),
  },
  feedbackContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: theme.spacing(0.5, 0),
  },
  feedbackButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.25),
  },
  feedbackButton: {
    padding: theme.spacing(0.5),
    color: '#4390FF',
    '&:hover': {
      backgroundColor: '#C7DFFC',
    },
    '&.active': {
      color: '#4390FF',
      backgroundColor: '#C7DFFC',
    },
    '&.Mui-disabled': {
      color: '#4390FF',
      opacity: 0.55,
    },
    '&.active.Mui-disabled': {
      opacity: 1,
    },
  },
  feedbackIcon: {
    display: 'block',
    width: 22,
    height: 20,
    objectFit: 'contain',
  },
  feedbackText: {
    fontWeight: 600,
    lineHeight: '20px',
    fontSize: '14px',
    color: '#333333',
    textAlign: 'center',
  },
  feedbackTags: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0, 0),
  },
  feedbackTag: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    height: 30,
    borderColor: '#777777',
    backgroundColor: '#FFFFFF',
    color: '#333333',
    '&&, &&:hover, &&:focus, &&:active': {
      backgroundColor: '#FFFFFF',
      borderColor: '#777777',
      color: '#333333',
    },
    '&&:focus-visible': {
      outline: '2px solid #4390FF',
      outlineOffset: 2,
    },
  },
  selectedFeedbackTag: {
    borderColor: '#4390FF',
    backgroundColor: '#C7DFFC',
    color: '#333333',
    '&&, &&:hover, &&:focus, &&:active': {
      backgroundColor: '#C7DFFC',
      borderColor: '#4390FF',
      color: '#333333',
    },
  },
  feedbackSubmitButton: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '22px',
    backgroundColor: '#4390FF',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#4390FF',
    },
    '&.Mui-disabled': {
      backgroundColor: '#4390FF',
      color: '#FFFFFF',
      opacity: 0.6,
    },
    minWidth: 150,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    textTransform: 'none',
  },
  feedbackComplete: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    margin: theme.spacing(-0.5, 0, 0.5),
  },
  feedbackCompleteIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: '#4390FF',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '16px',
  },
  feedbackCompleteText: {
    fontFamily: systemFontFamily,
    fontStyle: 'normal',
    textAlign: 'center',
    color: '#333333',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '20px',
  },
  sourcesSection: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  sourcesLabel: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16.8px',
    color: '#777777',
  },
  sourceLink: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16.8px',
    color: '#777777',
    textDecoration: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#EEEEEE',
    borderRadius: '12px',
    padding: theme.spacing(0.25, 0.75),
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:active': {
      opacity: 0.7,
    },
  },
}));

const feedbackTagOptions = ['accuracy', 'relevance', 'tone'];

const GeminiAssistant = ({ onFeedbackResult, placeDetails }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedOverview, setGeneratedOverview] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [selectedFeedbackTags, setSelectedFeedbackTags] = useState([]);
  const [sources, setSources] = useState([]);

  const handleGenerateOverview = async () => {
    setIsGenerating(true);

    track.analytics({
      category: 'gemini_assistant',
      data: {
        action: 'generate_overview',
        place_id: placeDetails._id,
      },
    });

    try {
      const requestData = {
        placeName: placeDetails.name || '',
        placeAddress: placeDetails.address || '',
        reviews: placeDetails.reviews || [],
      };

      const response = await api.geminiService.generateSummary(requestData);

      if (response?.success && response?.summary) {
        setGeneratedOverview(response.summary);
        setSources(response.sources || []);
      } else {
        setGeneratedOverview(t('messages.geminiGenerationError'));
      }
    } catch (error) {
      setGeneratedOverview(t('messages.geminiGenerationError'));
    } finally {
      setIsGenerating(false);
    }
  };

  const submitFeedback = async (feedbackType, feedbackTags = []) => {
    if (feedbackSubmitted || isSubmittingFeedback) return;

    setFeedback(feedbackType);
    onFeedbackResult(null);
    setIsSubmittingFeedback(true);

    try {
      await api.feedbackService.submitAiSummary({
        feedbackType,
        placeId: placeDetails._id,
        summary: generatedOverview,
        feedbackTags,
      });

      setFeedbackSubmitted(true);
      onFeedbackResult(null);
    } catch (error) {
      console.error('Unable to save Gemini feedback:', error);
      if (feedbackType) {
        setFeedback(null);
      }
      onFeedbackResult({
        message: t('messages.feedbackSubmissionError'),
        variant: 'error',
      });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleFeedback = (feedbackType) => {
    if (feedbackSubmitted || isSubmittingFeedback) return;

    if (feedbackType) {
      submitFeedback(true, []);
      return;
    }

    setFeedback(false);
    onFeedbackResult(null);
  };

  const handleFeedbackTag = (tag) => {
    setSelectedFeedbackTags((currentTags) =>
      currentTags.includes(tag)
        ? currentTags.filter((currentTag) => currentTag !== tag)
        : [...currentTags, tag]
    );
  };

  return (
    <Card className={classes.card} elevation={0}>
      <div className={classes.header}>
        <img
          src="/icons/gemini.svg"
          alt="Gemini AI"
          className={classes.geminiIcon}
        />
        <div className={classes.headerContent}>
          <Typography className={classes.title}>
            {t('messages.aiAccessibilityAssistant')}
          </Typography>
          <Typography className={classes.subtitle}>
            {t('messages.getInstantAnswers')}
          </Typography>
        </div>
        <Chip
          label={t('messages.beta')}
          size="small"
          className={classes.betaBadge}
        />
      </div>

      {!generatedOverview && (
        <Button
          className={`${classes.button} ${
            isGenerating ? classes.generatingButton : classes.generateButton
          }`}
          onClick={handleGenerateOverview}
          disabled={isGenerating}
          disableRipple
        >
          {isGenerating ? (
            <img
              src="/icons/gemini.svg"
              alt="Generating"
              className={classes.spinningIcon}
            />
          ) : (
            <img
              src="/icons/gemini.svg"
              alt="Gemini"
              className={classes.buttonIcon}
            />
          )}
          {isGenerating
            ? t('messages.generating')
            : t('messages.generateOverview')}
        </Button>
      )}

      {generatedOverview && !isGenerating && (
        <>
          <div className={classes.resultContainer}>
            <Typography className={classes.overviewText}>
              {generatedOverview}
            </Typography>

            {sources && sources.length > 0 && (
              <div className={classes.sourcesSection}>
                <Typography className={classes.sourcesLabel}>
                  Sources
                </Typography>
                {sources.map((source, index) => (
                  <a
                    key={index}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.sourceLink}
                    onClick={() => {
                      track.analytics({
                        category: 'gemini_assistant',
                        data: {
                          action: 'source_clicked',
                          source_url: source.url,
                          source_title: source.title,
                          place_id: placeDetails._id,
                        },
                      });
                    }}
                  >
                    {source.title}
                  </a>
                ))}
              </div>
            )}
          </div>
          {feedbackSubmitted ? (
            <div className={classes.feedbackComplete}>
              <span className={classes.feedbackCompleteIcon} aria-hidden="true">
                ✓
              </span>
              <Typography className={classes.feedbackCompleteText}>
                {t('messages.thankYouForYourFeedback')}
              </Typography>
            </div>
          ) : (
            <div className={classes.feedbackContainer}>
              <Typography className={classes.feedbackText}>
                {t(
                  feedback === false
                    ? 'messages.whatCouldBeImproved'
                    : 'messages.howWasThisResponse'
                )}
              </Typography>
              <div className={classes.feedbackButtons}>
                <IconButton
                  className={`${classes.feedbackButton} ${
                    feedback === true ? 'active' : ''
                  }`}
                  onClick={() => handleFeedback(true)}
                  disabled={isSubmittingFeedback}
                  aria-label={t('messages.positiveFeedback')}
                  size="small"
                >
                  <img
                    className={classes.feedbackIcon}
                    src={
                      feedback === true
                        ? '/images/feedbackIcons/Gemini_GoodResponse_Filled.svg'
                        : '/images/feedbackIcons/Gemini_GoodResponse_Unfilled.svg'
                    }
                    alt=""
                  />
                </IconButton>
                <IconButton
                  className={`${classes.feedbackButton} ${
                    feedback === false ? 'active' : ''
                  }`}
                  onClick={() => handleFeedback(false)}
                  disabled={isSubmittingFeedback}
                  aria-label={t('messages.negativeFeedback')}
                  size="small"
                >
                  <img
                    className={classes.feedbackIcon}
                    src={
                      feedback === false
                        ? '/images/feedbackIcons/Gemini_BadResponse_Filled.svg'
                        : '/images/feedbackIcons/Gemini_Bad_Response_Unfilled.svg'
                    }
                    alt=""
                  />
                </IconButton>
              </div>
              {feedback === false && (
                <>
                  <div className={classes.feedbackTags}>
                    {feedbackTagOptions.map((tag) => {
                      const isSelected = selectedFeedbackTags.includes(tag);

                      return (
                        <Chip
                          key={tag}
                          className={`${classes.feedbackTag} ${
                            isSelected ? classes.selectedFeedbackTag : ''
                          }`}
                          label={t(`messages.feedbackTag${tag}`)}
                          variant="outlined"
                          clickable
                          disabled={isSubmittingFeedback}
                          onClick={() => handleFeedbackTag(tag)}
                        />
                      );
                    })}
                  </div>
                  <Button
                    className={classes.feedbackSubmitButton}
                    color="primary"
                    variant="contained"
                    disabled={isSubmittingFeedback}
                    onClick={() => submitFeedback(false, selectedFeedbackTags)}
                  >
                    {t('messages.submitFeedback')}
                  </Button>
                </>
              )}
            </div>
          )}
          <Typography className={classes.disclaimer}>
            {t('messages.aiGeneratedDisclaimer')}
          </Typography>
        </>
      )}
    </Card>
  );
};

export default GeminiAssistant;
